// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore lodash is in the parent dir
import type { Dispatch, SetStateAction } from 'react';

import _ from 'lodash';

import { toast } from 'hooks/useToaster';
import { enableDATIndividualAccess } from 'lib/api/enableDATIndividualAccess';
import { getCustomers } from 'lib/api/getCustomers';
import { getLaneHistoryFromService } from 'lib/api/getLaneHistoryFromService';
import { getQuoteNumber } from 'lib/api/getQuoteNumber';
import {
  GreenscreensQuote,
  sendGreenscreensQuoteToService,
} from 'lib/api/postGreenscreensQuoteToService';
import {
  UserQuote,
  sendUserQuoteToService,
} from 'lib/api/postUserQuoteToService';
import { Maybe } from 'types/UtilityTypes';

import {
  FetchCustomersProps,
  FetchQuoteNumberProps,
  QuoteTypeInSource,
  SendGreenscreensQuoteProps,
  SendUserQuoteProps,
} from '../types';

/**
 * Sends Greenscreens quote data to the service
 */
export async function sendGreenscreensQuote({
  quote,
  setGreenscreensQuoteID,
}: SendGreenscreensQuoteProps): Promise<void> {
  const gsNetworkQuote = quote.quotes.find(
    (q) => q.type === QuoteTypeInSource.GS_Network
  );
  const gsBuyPowerQuote = quote.quotes.find(
    (q) => q.type === QuoteTypeInSource.GS_BuyPower
  );

  const greenscreensQuoteObject: GreenscreensQuote = {
    quoteRequestId: quote.quoteRequestId,
    stops: quote.stops,
    selectedRateName: quote.selectedRateName,
    networkLaneRateDistance: gsNetworkQuote?.distance || 0,
    networkLaneRateTargetBuy: gsNetworkQuote?.rates.targetPerMile || 0,
    networkLaneRateConfidenceLevel:
      gsNetworkQuote?.metadata?.confidenceLevel || 0,
    laneRateDistance: gsBuyPowerQuote?.distance || 0,
    laneRateTargetBuy: gsBuyPowerQuote?.rates.targetPerMile || 0,
    laneRateConfidenceLevel: gsBuyPowerQuote?.metadata?.confidenceLevel || 0,
  };
  const res = await sendGreenscreensQuoteToService(greenscreensQuoteObject);
  if (res.isOk()) {
    setGreenscreensQuoteID(res.value);
  }
}

/**
 * Sends user quote data to the service
 */
export async function sendUserQuote({
  email,
  quoteResponse,
  parentQuoteRequestId,
  greenscreensQuoteID,
  carrierCost,
  profit,
  profitType,
  finalPrice,
  draftResponse,
}: SendUserQuoteProps): Promise<void> {
  if (!greenscreensQuoteID || !email || !quoteResponse) return;

  const roundedFinalPrice = _.round(finalPrice);
  // TODO: Remove marginType and use profitType instead
  const userQuoteObject: UserQuote = {
    quoteRequestId: parentQuoteRequestId,
    gsQuoteID: greenscreensQuoteID,
    draftResponse: draftResponse,
    carrierCost: carrierCost,
    margin: profit,
    marginType: profitType,
    targetSell: roundedFinalPrice,
    stops: quoteResponse.stops,
  };

  await sendUserQuoteToService(userQuoteObject);
}

/**
 * Fetches quote number from the service
 */
export async function fetchQuoteNumber({
  email,
  setHasThirdPartyQuoteURLs,
  setValue,
}: FetchQuoteNumberProps): Promise<void> {
  if (!email) return;
  const res = await getQuoteNumber(email.id);
  if (res.isOk()) {
    setHasThirdPartyQuoteURLs(res.value.hasThirdPartyQuoteURLs);
    // can't use resetField here because the form input hasn't been initialized yet
    setValue('quoteNumber', res.value.quoteNumber);
    return;
  }
  setHasThirdPartyQuoteURLs(false);
}

/**
 * Fetches lane history from external service
 */
export async function fetchLaneHistoryFromService(params: {
  quoteRequestId: number;
  originCity: string;
  originState: string;
  originZip: string;
  originCountry: string;
  destinationCity: string;
  destinationState: string;
  destinationZip: string;
  destinationCountry: string;
  transportType: string;
}) {
  const res = await getLaneHistoryFromService(params);
  return res;
}

/**
 * Fetches customer list from TMS
 */
export async function fetchCustomers({
  setInitialCustomers,
  setCustomers,
  setTMSTenant,
  tmsIntegrations,
}: FetchCustomersProps): Promise<void> {
  const res = await getCustomers(tmsIntegrations?.[0]?.id);
  if (res.isOk()) {
    setInitialCustomers(res.value.customerList);
    setCustomers(res.value.customerList);
    setTMSTenant(res.value.tmsTenant);
    return;
  }

  toast({
    description: 'Error while fetching customer list.',
    variant: 'destructive',
  });
}

/**
 * Enables DAT individual access for a user
 */
export async function enableDATAccess({
  datEmailAddress,
  setHasGrantedDATPermissions,
}: {
  datEmailAddress: string;
  setHasGrantedDATPermissions: Dispatch<SetStateAction<Maybe<boolean>>>;
}): Promise<void> {
  const res = await enableDATIndividualAccess(datEmailAddress);
  if (res.isOk()) {
    setHasGrantedDATPermissions(true);
    toast({
      description: 'Successfully enabled DAT!',
      variant: 'success',
    });
    return;
  }

  const lowercaseError = res.error.message.toLowerCase();
  const isConnexionError = lowercaseError.includes(
    'does not have a dat connexion seat'
  );

  const title = isConnexionError ? 'DAT reported an issue' : 'Error';
  const description = isConnexionError
    ? `Please input an email address that has a DAT Connexion seat. ` +
      `If you're unsure, please contact the DAT admin at your company.`
    : 'Failed to validate DAT credentials';

  toast({
    title: title,
    description: description,
    variant: 'destructive',
  });
}
